/**
 * Inversion Acknowledgment Panel
 *
 * Surfaces prioritization inversions detected in a ranked Priority List and
 * requires explicit acknowledgment (with rationale) before the set can
 * be published as a Plan of Record.
 *
 * An inversion is: item A is ranked higher than item B, but A has a hard
 * dependency on B. This does not change the rank — it requires the operator
 * to confirm they understand the implication and record why it is acceptable.
 *
 * This panel belongs between the ranked items list and the Publish action
 * in PrioritySetGovernance.
 *
 * Canon: DOCS_V4/00-canon/01-terminology-and-glossary_v3.md
 *        "Prioritization Inversion"
 */

import { useState, useEffect, useCallback } from 'react';
import { AlertTriangle, CheckCircle, ChevronDown, ChevronRight, ArrowDown } from 'lucide-react';
import {
  detectPrioritizationInversions,
  getUnacknowledgedInversions,
  type PrioritizationInversion,
  type InversionAcknowledgment,
} from '../../../lib/prioritizationInversionDetector';
import {
  loadInversionAcknowledgments,
  loadHardDependencyEdgesForItems,
  acknowledgeInversion,
} from '../../../lib/inversionAcknowledgmentService';
import type { ItemRelationshipEdge } from '../../../types/prioritization/itemRelationships';

// ============================================================================
// Props
// ============================================================================

interface Item {
  id: string;
  title: string;
}

export interface InversionAcknowledgmentPanelProps {
  prioritySetId: string;
  rankedItemIds: string[];
  /** All items needed to display titles. At minimum, the ranked items. */
  items: Item[];
  actorId: string;
  /** Called whenever the acknowledged count changes, so parent can update publish gate. */
  onAcknowledgmentChange?: (allAcknowledged: boolean) => void;
}

// ============================================================================
// Sub-components
// ============================================================================

interface InversionRowProps {
  inversion: PrioritizationInversion;
  items: Item[];
  acknowledgment?: InversionAcknowledgment;
  prioritySetId: string;
  actorId: string;
  onAcknowledged: (ack: InversionAcknowledgment) => void;
}

function InversionRow({
  inversion,
  items,
  acknowledgment,
  prioritySetId,
  actorId,
  onAcknowledged,
}: InversionRowProps) {
  const [expanded, setExpanded] = useState(!acknowledgment);
  const [rationale, setRationale] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const higherItem = items.find((i) => i.id === inversion.higherRankedItemId);
  const lowerItem = items.find((i) => i.id === inversion.lowerRankedItemId);

  const isAcknowledged = !!acknowledgment;

  const handleAcknowledge = async () => {
    if (!rationale.trim()) return;
    setIsSaving(true);
    setSaveError(null);

    const result = await acknowledgeInversion({
      prioritySetId,
      inversion,
      acknowledgedBy: actorId,
      rationale: rationale.trim(),
    });

    setIsSaving(false);

    if (!result.success || !result.acknowledgment) {
      setSaveError(result.error ?? 'Failed to save acknowledgment');
      return;
    }

    onAcknowledged(result.acknowledgment);
    setExpanded(false);
  };

  return (
    <div
      className={`border  overflow-hidden ${
        isAcknowledged ? 'border-[var(--green)] bg-[var(--green-dim)]' : 'border-[var(--mustard)] bg-[var(--mustard-dim)]'
      }`}
    >
      {/* Header row */}
      <button
        type="button"
        className="w-full flex items-start gap-3 p-4 text-left"
        onClick={() => setExpanded((v) => !v)}
      >
        {isAcknowledged ? (
          <CheckCircle className="h-5 w-5 text-[var(--green)] mt-0.5 flex-shrink-0" />
        ) : (
          <AlertTriangle className="h-5 w-5 text-[var(--mustard)] mt-0.5 flex-shrink-0" />
        )}

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium text-[var(--text-pri)]">
              #{inversion.higherRank} {higherItem?.title ?? inversion.higherRankedItemId}
            </span>
            <ArrowDown className="h-3.5 w-3.5 text-[var(--text-ter)] flex-shrink-0" />
            <span className="text-sm text-[var(--text-sec)]">depends on</span>
            <ArrowDown className="h-3.5 w-3.5 text-[var(--text-ter)] flex-shrink-0" />
            <span className="text-sm font-medium text-[var(--text-pri)]">
              #{inversion.lowerRank} {lowerItem?.title ?? inversion.lowerRankedItemId}
            </span>
          </div>
          <p className="text-xs text-[var(--text-ter)] mt-0.5">
            The higher-priority item has a hard dependency on a lower-priority item.
          </p>
        </div>

        <div className="flex-shrink-0 ml-2">
          {expanded ? (
            <ChevronDown className="h-4 w-4 text-[var(--text-ter)]" />
          ) : (
            <ChevronRight className="h-4 w-4 text-[var(--text-ter)]" />
          )}
        </div>
      </button>

      {/* Expanded body */}
      {expanded && (
        <div className="px-4 pb-4 border-t border-[var(--mustard)]">
          {isAcknowledged ? (
            <div className="mt-3">
              <p className="text-xs font-medium text-[var(--text-ter)] uppercase tracking-wide mb-1">
                Acknowledged rationale
              </p>
              <p className="text-sm text-[var(--text-sec)] italic">"{acknowledgment.rationale}"</p>
              <p className="text-xs text-[var(--text-ter)] mt-1">
                By {acknowledgment.acknowledgedBy} ·{' '}
                {acknowledgment.acknowledgedAt.toLocaleString()}
              </p>
            </div>
          ) : (
            <div className="mt-3 space-y-3">
              <div className="bg-[var(--bg-raised)] border border-[var(--mustard)] p-3">
                <p className="text-sm text-[var(--text-sec)]">
                  <span className="font-medium">
                    #{inversion.higherRank} {higherItem?.title ?? 'Item'}
                  </span>{' '}
                  is ranked higher than{' '}
                  <span className="font-medium">
                    #{inversion.lowerRank} {lowerItem?.title ?? 'Item'}
                  </span>
                  , but has a hard dependency on it. This does not change the ranking — it means
                  execution must address{' '}
                  <span className="font-medium">{lowerItem?.title ?? 'the dependency'}</span> before{' '}
                  <span className="font-medium">{higherItem?.title ?? 'this item'}</span> can proceed.
                </p>
              </div>

              <div>
                <label className="block text-xs font-medium text-[var(--text-sec)] mb-1">
                  Why is this ranking acceptable given the dependency?{' '}
                  <span className="text-[var(--coral)]">*</span>
                </label>
                <textarea
                  className="w-full text-sm border border-[var(--rule)] px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent resize-none"
                  rows={3}
                  placeholder="e.g. The dependency is a short-cycle task that will be resolved before execution begins. Ranking reflects strategic value, not execution order."
                  value={rationale}
                  onChange={(e) => setRationale(e.target.value)}
                />
              </div>

              {saveError && (
                <p className="text-sm text-[var(--coral)]">{saveError}</p>
              )}

              <button
                type="button"
                disabled={!rationale.trim() || isSaving}
                onClick={handleAcknowledge}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium bg-[var(--mustard)] text-white hover:bg-[var(--mustard)] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSaving ? 'Saving…' : 'Acknowledge inversion'}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Main panel
// ============================================================================

export function InversionAcknowledgmentPanel({
  prioritySetId,
  rankedItemIds,
  items,
  actorId,
  onAcknowledgmentChange,
}: InversionAcknowledgmentPanelProps) {
  const [inversions, setInversions] = useState<PrioritizationInversion[]>([]);
  const [acknowledgments, setAcknowledgments] = useState<InversionAcknowledgment[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Load inversions and existing acknowledgments
  useEffect(() => {
    if (rankedItemIds.length === 0) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    (async () => {
      setIsLoading(true);

      const [rawEdges, acks] = await Promise.all([
        loadHardDependencyEdgesForItems(rankedItemIds),
        loadInversionAcknowledgments(prioritySetId),
      ]);

      if (cancelled) return;

      // Shape raw DB rows into the minimal form the detector needs
      const edges: ItemRelationshipEdge[] = rawEdges.map((e) => ({
        id: e.id,
        type: e.type,
        subtype: (e.subtype as 'hard' | 'soft' | 'gate' | undefined) ?? undefined,
        directionality: 'directed',
        status: 'active',
        declaredMode: 'declared',
        sourceItemId: e.sourceItemId,
        targetItemId: e.targetItemId,
        createdAt: new Date(),
      }));

      const detected = detectPrioritizationInversions(rankedItemIds, edges);
      setInversions(detected);
      setAcknowledgments(acks);
      setIsLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [prioritySetId, rankedItemIds]);

  // Notify parent when acknowledgment state changes
  useEffect(() => {
    if (isLoading) return;
    const unacked = getUnacknowledgedInversions(inversions, acknowledgments);
    onAcknowledgmentChange?.(unacked.length === 0);
  }, [inversions, acknowledgments, isLoading, onAcknowledgmentChange]);

  const handleAcknowledged = useCallback((ack: InversionAcknowledgment) => {
    setAcknowledgments((prev) => {
      const filtered = prev.filter(
        (a) =>
          !(
            a.higherRankedItemId === ack.higherRankedItemId &&
            a.lowerRankedItemId === ack.lowerRankedItemId
          ),
      );
      return [...filtered, ack];
    });
  }, []);

  // Nothing to show if no inversions
  if (!isLoading && inversions.length === 0) return null;

  const unacked = getUnacknowledgedInversions(inversions, acknowledgments);
  const allAcknowledged = !isLoading && unacked.length === 0;

  return (
    <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6 mb-6">
      <div className="flex items-start gap-3 mb-4">
        {allAcknowledged ? (
          <CheckCircle className="h-5 w-5 text-[var(--green)] mt-0.5 flex-shrink-0" />
        ) : (
          <AlertTriangle className="h-5 w-5 text-[var(--mustard)] mt-0.5 flex-shrink-0" />
        )}
        <div>
          <h2 className="text-lg font-semibold text-[var(--text-pri)]">
            Prioritization Inversions
            {!isLoading && (
              <span className="ml-2 text-sm font-normal text-[var(--text-ter)]">
                {allAcknowledged
                  ? `${inversions.length} acknowledged`
                  : `${unacked.length} of ${inversions.length} require acknowledgment`}
              </span>
            )}
          </h2>
          <p className="text-sm text-[var(--text-sec)] mt-0.5">
            {allAcknowledged
              ? 'All dependency inversions have been acknowledged. The ranked order and dependency implications are on record.'
              : 'One or more items are ranked above items they depend on. This does not change the ranking — each inversion must be acknowledged with a rationale before this set can be published.'}
          </p>
        </div>
      </div>

      {isLoading ? (
        <div className="text-sm text-[var(--text-ter)] py-2">Checking for dependency inversions…</div>
      ) : (
        <div className="space-y-3">
          {inversions.map((inv) => {
            const ack = acknowledgments.find(
              (a) =>
                a.higherRankedItemId === inv.higherRankedItemId &&
                a.lowerRankedItemId === inv.lowerRankedItemId,
            );
            return (
              <InversionRow
                key={`${inv.higherRankedItemId}::${inv.lowerRankedItemId}`}
                inversion={inv}
                items={items}
                acknowledgment={ack}
                prioritySetId={prioritySetId}
                actorId={actorId}
                onAcknowledged={handleAcknowledged}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}
